<script lang="ts" setup>
import 'deep-chat'
import { ref, onMounted, watch, CSSProperties, ComputedRef } from 'vue'
import { Role, TMessageContent } from './types'
import { Signals } from 'deep-chat/dist/types/handler'
import { MessagesApi } from '@/api/modules/message'
import { useThreadsStore } from '@/store/threads-store'
import { useChatHistoryStore } from '@/store/chat-history'
import { useServerSentEvents } from '@/composables/use-server-sent-events'
import { storeToRefs } from 'pinia'
import { FileApi } from '@/api/modules/files'
import { useS3Upload } from '@/composables/use-s3-upload'
import { ISendMessageRequest } from '@/api/modules/message/types'
import { useAuthStore } from '@/store/auth-store'
import { useI18n } from 'vue-i18n'
import { copyToClipboard } from '@/utils/clipboard'
import { useAppNotification } from '@/composables/use-app-notification'
import { useAgentsLibraryStore } from '@/store/task-library'
import { IAgent } from '@/api/modules/task-library/types'
import { useScreenSize } from '@/composables/use-screen-size'

const { t, locale } = useI18n()
const atFirstLoad = ref(false)

const { IS_MOBILE } = useScreenSize()


interface Props {
  threadId: string
  isShowGreeting?: boolean
}

const props = defineProps<Props>()

const authStore = useAuthStore()
const { token } = storeToRefs(authStore)
const { success } = useAppNotification()

const allowedFileTypes = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.png',
  '.jpg',
  '.jpeg',
  '.csv',
  '.doc',
  '.docx',
  '.txt',
]

const threadsStore = useThreadsStore()
const { thread } = storeToRefs(threadsStore)

const agentsLibraryStore = useAgentsLibraryStore()
const { agentsItems } = storeToRefs(agentsLibraryStore)

const agent = computed(() => {
  return agentsItems.value.find((a: IAgent) => a.name == thread.value?.agent_name)
})

const { uploadFiles, setFileTypes } = useS3Upload({
  presignedUrlEndpoint: `${import.meta.env.VITE_API_URL}/files/presign`,
  maxFileSize: 10 * 1024 * 1024, // 10MB
  fileTypes: allowedFileTypes,
  multiple: true,
  headers: {
    Authorization: `Bearer ${token.value}`,
    'Accept-Language': locale.value,
  },
})

const { on, connect, disconnect, isConnecting, clearEvents, setupSignal } =
  useServerSentEvents<Notification>(
    `${import.meta.env.VITE_API_URL}/events?thread_id=${props.threadId}`,
    {
      immediate: false,
      autoReconnect: false,
      format: 'json',
      headers: {
        Authorization: `Bearer ${token.value}`,
        'Accept-Language': locale.value,
      },
      debug: true,
      transform: (data: any) => ({
        ...data,
        timestamp: new Date(data.timestamp),
      }),
    },
  )

const { setCurrentThread } = useThreadsStore()

const chatHistoryStore = useChatHistoryStore()

const {
  removeMessageHistoryById,
  updateMessage,
  getMessageHistoryById,
  getThreadMessages,
  clearThreadMessages,
  addNewMessage,
  createChatMessageData,
  createActionData,
  createHtmlMessage,
} = chatHistoryStore

const { messageHistory, hasMmore } = storeToRefs(chatHistoryStore)

const cacheMessages = ref<any[]>()

const chatElementRef = ref<any>()

const isLoading = ref<boolean>(false)

const isNeedUpdate = ref<boolean>(false)

const fileConnect = computed<any>(() => ({
  button: {
    position: 'inside-left',
    styles: {
      text: {
        content: t('ui.chat-upload-button-title'),
      },
    },
  },

  files: { acceptedFormats: (agent.value?.file_upload_extensions || allowedFileTypes).join(',') },
}))

const submitUserMessage = (objMessage: any) => {
  if (!chatElementRef.value) return
  chatElementRef.value.submitUserMessage(objMessage)
}

watch(
  () => props.threadId,
  async (newValue?: string, oldVaue?: string) => {
    if (newValue && newValue !== oldVaue) {
      await handleGetThreadMessages(newValue)
    }
  },
)

watch(
  () => agent.value?.file_upload_extensions,
  (value) => setFileTypes(value || allowedFileTypes),
)

watch(
  () => isConnecting.value,
  (value: boolean) => {
    if (chatElementRef.value) {
      chatElementRef.value.disableSubmitButton(value)
    }
  },
)

onMounted(async () => {
  if (chatElementRef.value) {
    //chatElementRef.value.disableSubmitButton(true)
    console.log('onMounted')
  }

  ;(window as any).action = async function (element: HTMLElement) {
    if (chatElementRef.value) {
      const msgAction = element.dataset['action']
      const dataIdElement =
          element.parentElement?.parentElement?.querySelector<HTMLHtmlElement>('[data-id]')
      const msgId = dataIdElement ? dataIdElement.dataset['id'] : undefined

      switch (msgAction) {
        case 'action:retry': {
          if (msgId) {
            if (chatElementRef.value) {
              chatElementRef.value.submitUserMessage({
                text: dataIdElement ? dataIdElement.innerText : undefined,
                custom: { id: msgId, retry: true },
              })

              removeMessageHistoryById(msgId)
              isNeedUpdate.value = true
            }
          }
          return
        }

        case 'action:copy': {
          if (dataIdElement) {
            const result = await copyToClipboard(dataIdElement.innerText)

            if (result) {
              success(t('ui.notity-success-title'), t('ui.notify-clipboard-copied'))
            }
          }
          return
        }

        default:
          let message = element.innerText
          chatElementRef.value.submitUserMessage({
            text: message,
            custom: {
              action: msgAction,
            },
          })
      }
    }
  }
})

const loadHistory = async (index: number) => {
  if (messageHistory.value.length === 0) return []
  if (index === 0 && atFirstLoad.value === false) return [!hasMmore.value]

  const firstMessage = messageHistory.value[0]
  const res = await getThreadMessages(props.threadId, firstMessage?.custom?.message_id)

  atFirstLoad.value = true

  return res
}

const connectHandler = async (body: any, signals: Signals) => {
  setupSignal(signals)

  signals.onOpen()

  try {
    let threadId = props.threadId
    let newMessage: Partial<ISendMessageRequest> = {}

    if (body instanceof FormData) {
      const filesData = (body as FormData).getAll('files')

      if (filesData && filesData.length > 0) {
        const result = await uploadFiles(filesData as File[])

        const filesResult = await Promise.all(
            (result.fileIds || []).map(
                (id, index) => id && FileApi.uploaded(id, result.sizes[index]),
            ),
        ).catch((e) => console.log(e))

        newMessage = {
          files: (filesResult || []).map((fr: any) => ({
            name: fr.filename,
            src: fr.download_url,
            type: 'file',
          })),
          files_id: (filesResult || []).map((fr: any) => fr.file_id).filter((f) => f !== undefined),
        }
      }

      const messageData = (body as FormData).get('message1')

      if (messageData) {
        const message = JSON.parse(messageData.toString())

        newMessage.text = message.text || ''
      }
    } else {
      const messages = body.messages
      newMessage.text = messages[0]?.text

      if (messages[0]?.custom && messages[0]?.custom?.action) {
        newMessage.action = messages[0]?.custom?.action
      }

      if (messages[0]?.custom && messages[0]?.custom?.retry) {
        newMessage.retry = true
        newMessage.message_id = messages[0]?.custom?.id
      }
    }

    const r = await MessagesApi.sendMessage({
      thread_id: props.threadId || '',
      data: {
        text: newMessage.text || '',
        type: 'text',
        ...(newMessage.files_id ? { files_id: newMessage.files_id } : null),
        ...(newMessage.action ? { action: newMessage.action } : null),
        ...(newMessage.message_id ? { message_id: newMessage.message_id } : null),
        ...(newMessage.retry ? { retry: newMessage.retry } : null),
      },
    })

    const newTreadTitle = newMessage?.text
      ? newMessage?.text
      : newMessage?.files && newMessage?.files?.length > 0
        ? newMessage.files[0].name
        : t('menu.new-chat')

    setCurrentThread({
      thread_id: props.threadId,
      modified_at: '',
      title: newTreadTitle,
      last_message: newTreadTitle,
    })

    if (newMessage?.files && newMessage?.files.length > 0) {
      isNeedUpdate.value = true
    }

    // signals.onOpen()
  } catch (e) {
    console.log(e)
    signals.onResponse({ error: t('error.chat-message-error') })
  }
}

const handleGetThreadMessages = async (threadId: string) => {
  try {
    isLoading.value = true
    clearThreadMessages()
    await getThreadMessages(threadId)

    cacheMessages.value = messageHistory.value
  } catch (e) {
    console.log(e)
  } finally {
    isLoading.value = false
  }
}

defineExpose({
  submitUserMessage: submitUserMessage,
})

onBeforeUnmount(() => {
  clearEvents()
  disconnect()
})

onMounted(async () => {
  if (props.threadId) {
    await handleGetThreadMessages(props.threadId)
  }

  if (chatElementRef.value) {
    chatElementRef.value.scrollToBottom()
    chatElementRef.value.loadHistory = loadHistory

    on('message.created', ({ data, signals }: any) => {
      if (data.author === 'assistant') {
        const message = createChatMessageData(data)
        // signals.onResponse(message)
        chatElementRef.value.addMessage(message)

        addNewMessage(message)

        if (data.body.actions && data.body.actions.length > 0) {
          const actionMessage = createActionData(data)

          addNewMessage({
            ...data.body,
            ...actionMessage.body,
            attachments: [],
            files: [],
            text: undefined,
            role: Role.AiActions,
            custom: {
              message_id: data.body.message_id,
            },
          })

          chatElementRef.value.addMessage({ ...actionMessage.body, role: Role.AiActions })

          if (isNeedUpdate.value) {
            cacheMessages.value = messageHistory.value
            isNeedUpdate.value = false
          }
        }
        if (data.status !== 'processing') {
          signals.onClose()
        }
      } else if (data.author === Role.User) {
        const message = createChatMessageData(data)
        addNewMessage(message)

        if (data.body?.attachments?.length > 0) {
          return
        } else {
          const messages = chatElementRef.value.getMessages()
          chatElementRef.value.updateMessage(message, messages.length - 1)
        }
      }
    })

    on('message.completed', ({ data, signals }: any) => {
      if (messageHistory.value && messageHistory.value.length > 0) {
        const lastMessage = messageHistory.value[messageHistory.value.length - 1]

        setCurrentThread({
          thread_id: props.threadId,
          modified_at: '',
          title: lastMessage.custom?.o_text,
          last_message: lastMessage.custom?.o_text,
        })
      }

      if (data.author === 'assistant') {
        if (data.status === 'ready' && getMessageHistoryById(data.message_id)) {
          cacheMessages.value = messageHistory.value

          signals.onClose()
          return
        }

        signals.onResponse(data.body)

        addNewMessage({
          ...data.body,
          role: Role.Ai,
        })

        signals.onClose()
      }
    })

    on('message.delta', (event: any) => {
      if (chatElementRef.value) {
        const messages = chatElementRef.value.getMessages()
        const lastIndex = messages.length - 1

        const orginMessage = getMessageHistoryById(event.data.message_id)
        let updated_text = ''
        if (orginMessage && orginMessage.custom) {
          orginMessage.custom = {
            ...orginMessage?.custom,
            o_text: orginMessage?.custom.o_text + event.data.delta,
          }

          updated_text = orginMessage?.custom.o_text
          orginMessage.text = '\n\n' + updated_text

          updated_text = createHtmlMessage({
            status: orginMessage.status,
            message_id: event.data.message_id,
            text: orginMessage.text,
            html: orginMessage.html,
          })

          updateMessage(
            {
              text: createHtmlMessage({
                status: orginMessage.status,
                message_id: event.data.message_id,
                text: orginMessage.text,
                html: orginMessage.html,
              }),
              custom: orginMessage?.custom,
            },
            event.data.message_id,
          )
        }

        chatElementRef.value.updateMessage(
          {
            text: updated_text,
          },
          lastIndex,
        )

        // chatElementRef.value.scrollToBottom()
      }
    })

    on('open', (event: any) => {
      if (event.signals) event.signals.onOpen()
    })

    on('close', (event: any) => {
      if (event.signals) event.signals.onClose()
    })

    on('error', (event: any) => {
      console.log('error', event)
      if (chatElementRef.value) {
        if (event.data.body?.text) {
          updateMessage(
            {
              text: createHtmlMessage({
                status: event.data.status,
                message_id: event.data.message_id,
                text: event.data.body?.text,
                html: event.data.body?.html,
              }),
            },
            event.data.message_id,
          )
        }

        messageHistory.value = messageHistory.value.filter((m: any) => !m.error)

        addNewMessage({ error: 'error', custom: { message_id: event.data.message_id } })

        cacheMessages.value = messageHistory.value

        if (event.signals) {
          // event.signals.onResponse({ error: 'error' })
          event.signals.onClose()
        } else {
          // chatElementRef.value.addMessage({ error: 'error' })
        }
      }
    })

    connect()
  }

  // chatElementRef.scrollToBottom();
})

const basTextInputContainerStyles: ComputedRef<CSSProperties> = computed(() => (
    {
      borderRadius: '16px',
      width: '78%',
      boxShadow: 'initial',
      backgroundColor: 'var(--gray_0)',
      border: 'none',
      marginTop: '0px',
      padding: '16px 16px 32px 16px',

    }
))

const textInputContainerStyles: ComputedRef<CSSProperties> = computed(() => (
    {
      ...basTextInputContainerStyles.value,
      ...(IS_MOBILE.value ? {
                height: 'auto',
                minHeight: '24px',
                maxHeight: '90px',
                overflowY: 'auto',
      } : {
        height: '120px',
      })
    }
))

const textInputStyles: CSSProperties = {
  padding: '0px',
  paddingLeft: '0px',
  paddingRight: '0px',
}

const introPanelStyle: CSSProperties = {
  position: 'initial',
  display: 'block',
  padding: '60px 160px',
  textAlign: 'center',
}

const inputPlaceholderStyle: CSSProperties = {
  color: 'var(--gray_40)',
}

const aiBubbleStyles: CSSProperties = {
  backgroundColor: 'initial',
  color: 'var(--gray_100)',
  borderRadius: '8px',
  maxWidth: '85%',
}

const userBubbleStyles: CSSProperties = {
  backgroundColor: 'initial',
}

const attachmentContainerStyles: CSSProperties = {
  backgroundColor: 'var(--gray_0)',
  width: '78%',
  padding: '10px 16px',
  top: '-3.3em',
  borderRadius: '16px 16px 0 0',
}

const chatPlaceholder = computed<string>(() => t('ui.chat-input-placeholder'))

const errorMessages = computed<Object>(() => ({
  overrides: {
    default: t('error.chat-default-error'),
    service: t('error.chat-service-error'),
  },
}))

const auxiliaryStyles = `
h1, h2, h3 {
  line-height: 1.1em;
}
#chat-view {
  width: 100% !important;
}

#messages > *:not(.intro-panel) {
  max-width: 1200px;
  margin: 0 auto !important;
}

.intro-panel {
max-width: 1200px;
margin: auto !important;
}

#file-attachment-container {
top: 0 !important;
}

#input {
  padding-top: 16px;
}

#input:has(#file-attachment-container > .file-attachment > .remove-file-attachment-button) {
  padding-top: 60px;
}

#input > * {
  max-width: 1100px;
}

.status {
  position: relative;
  height: 16px;
  width: 16px;
  left: calc(100% - 16px);
}
.status.created {
    background: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxNiAxNiI+PHBvbHlnb24gcG9pbnRzPSI3LjYgMTUuMTUgMC44MyAxMC42NCAxLjM5IDkuODEgNy4zMyAxMy43NyAxNC4zNCAzLjMzIDE1LjE3IDMuODkgNy42IDE1LjE1IiBmaWxsPSIjNGQ0ZDRkIi8+PHJlY3Qgd2lkdGg9IjE2IiBoZWlnaHQ9IjE2IiBmaWxsPSJub25lIi8+PC9zdmc+');
}
.status.completed {
    background: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxNiAxNiI+PHBvbHlnb24gcG9pbnRzPSI3LjYgMTUuMTUgMC44MyAxMC42NCAxLjM5IDkuODEgNy4zMyAxMy43NyAxNC4zNCAzLjMzIDE1LjE3IDMuODkgNy42IDE1LjE1IiBmaWxsPSIjNGQ0ZDRkIi8+PHJlY3Qgd2lkdGg9IjE2IiBoZWlnaHQ9IjE2IiBmaWxsPSJub25lIi8+PHBvbHlnb24gcG9pbnRzPSI3LjExIDExLjgxIDIuMzggOC42NyAyLjkzIDcuODQgNi44NCAxMC40MiAxMi40NSAyLjA0IDEzLjI4IDIuNiA3LjExIDExLjgxIiBmaWxsPSIjNGQ0ZDRkIi8+PC9zdmc+');
}
.status.error {
    background: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxNiAxNiI+PHJlY3Qgd2lkdGg9IjE2IiBoZWlnaHQ9IjE2IiBmaWxsPSJub25lIi8+PHBhdGggZD0iTTguMDgsMTUuNDlhNy4zNCw3LjM0LDAsMSwxLDcuMzMtNy4zNEE3LjM1LDcuMzUsMCwwLDEsOC4wOCwxNS40OVptMC0xMy42OGE2LjM0LDYuMzQsMCwxLDAsNi4zMyw2LjM0QTYuMzQsNi4zNCwwLDAsMCw4LjA4LDEuODFaIiBmaWxsPSIjZmYwZDBkIi8+PHBhdGggZD0iTTcuMTksMTIuNWEuOTEuOTEsMCwwLDEsLjkyLTEsLjg5Ljg5LDAsMCwxLC45MSwxLC45LjksMCwwLDEtLjkxLDFBLjkxLjkxLDAsMCwxLDcuMTksMTIuNVptLjM5LTIuMjJMNy4zNiwyLjgzSDguODVsLS4yMSw3LjQ1WiIgZmlsbD0iI2ZmMGQwZCIvPjwvc3ZnPg==');
}

.emptyContent {
  text-align: center;
  max-width: 800px;
  margin: 0 40px;
}

.command {
  display:flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 28px;
}
.command .command__button {
  border: 1px var(--gray_20) solid;
  box-shadow: initial;
  background-color: var(--gray_white);
  padding: 4px 12px;
  border-radius: 6px;
  cursor: pointer;
}

  .file-attachment {
    width: 180px;
    background-color: transparent;
}

  .message-bubble .deep-chat-temporary-message {
    display: flex;
    gap: 8px;
}

  .message-bubble {
    margin-bottom: 16px;
}

  .message-bubble .html {
    display: flex !important;
    flex-direction: column;
    position: relative;
}

  .message-bubble .html .html-message-user,
  .message-bubble .html .html-message-ai,
  .message-bubble.user-message-text {
      display: inline-block;
      color: var(--gray_100);
      padding: 8px;
}

  .message-bubble .html .html-message-user, .message-bubble.user-message-text {
    background-color: var(--gray_0);
    border-radius: 8px;
}

  .message-bubble .html:has(.html-message-user) .html-message-action,
  .message-bubble .html:has(.html-message-ai) .html-message-action {
    position: absolute;
    display: flex !important;
    flex-wrap: nowrap;
    justify-content: flex-end;
    gap:4px;
    bottom: -28px;
 }

  .message-bubble .html:has(.html-message-user) .html-message-action {

    right:0px
}

  .message-bubble .html-message-action > button {
    display: none;
    border: none;
    cursor: pointer;
    border-radius: 4px;
    height: 24px;
    width: 24px;
    background-color: var(--gray_0);
    color: var(--gray_100);
    padding: 2px;
}

  .inner-message-container .message-bubble .html-message-action > button > i {
    display: block;
    height: 16px;
    width: 16px;
    margin: auto;
    opacity: 0.5;
}

  .inner-message-container .message-bubble .html-message-action {
    opacity: 0;
    transition: opacity 0.3s ease;
}

  .inner-message-container:hover .message-bubble .html-message-action {
    opacity: 1;
}

  .inner-message-container:hover .message-bubble  .html-message-action > button[data-action="action:retry"] {
    display: none;
}

  .inner-message-container:hover .message-bubble .html-message-action > button i.copy {
    background-image: url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDI0LjEuMCwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9ItCh0LvQvtC5XzEiIGZvY3VzYWJsZT0iZmFsc2UiCgkgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayIgeD0iMHB4IiB5PSIwcHgiIHZpZXdCb3g9IjAgMCAxMiAxMiIgeG1sOnNwYWNlPSJwcmVzZXJ2ZSI+CjxwYXRoIGQ9Ik0xMC4zLDBIMy4xQzMsMCwzLDAsMywwLjF2MC44QzMsMC45LDMsMSwzLjEsMWg2LjZ2OS4yYzAsMC4xLDAsMC4xLDAuMSwwLjFoMC44YzAuMSwwLDAuMSwwLDAuMS0wLjFWMC40CglDMTAuNywwLjIsMTAuNSwwLDEwLjMsMHogTTguNiwxLjdIMS43Yy0wLjIsMC0wLjQsMC4yLTAuNCwwLjR2Ny4xYzAsMC4xLDAsMC4yLDAuMSwwLjNsMi4zLDIuM2MwLDAsMC4xLDAuMSwwLjEsMC4xdjBoMC4xCgljMCwwLDAuMSwwLDAuMSwwaDQuNUM4LjgsMTIsOSwxMS44LDksMTEuNlYyLjFDOSwxLjksOC44LDEuNyw4LjYsMS43eiBNMy44LDEwLjZMMi43LDkuNWgxLjJWMTAuNnogTTgsMTFINC43VjkuMQoJYzAtMC4zLTAuMi0wLjUtMC41LTAuNUgyLjJWMi43SDhWMTF6Ii8+Cjwvc3ZnPgo=);
}

  .inner-message-container:hover .message-bubble .html-message-action > button i.retry {
    background-image: url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDI0LjEuMCwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9ItCh0LvQvtC5XzEiIGZvY3VzYWJsZT0iZmFsc2UiCgkgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayIgeD0iMHB4IiB5PSIwcHgiIHZpZXdCb3g9IjAgMCAxMiAxMiIKCSBzdHlsZT0iZW5hYmxlLWJhY2tncm91bmQ6bmV3IDAgMCAxMiAxMjsiIHhtbDpzcGFjZT0icHJlc2VydmUiPgo8cGF0aCBkPSJNNiwwQzIuNywwLDAsMi43LDAsNmMwLDEuNywwLjcsMy4yLDEuOCw0LjNsLTAuNywwLjhsMi43LDAuMkwzLjUsOC41TDIuNyw5LjRDMS44LDguNSwxLjIsNy4zLDEuMiw2YzAtMi42LDIuMS00LjgsNC44LTQuOAoJczQuOCwyLjEsNC44LDQuOGMwLDEuNy0wLjksMy4yLTIuMiw0TDkuMiwxMUMxMC45LDEwLDEyLDguMSwxMiw2QzEyLDIuNyw5LjMsMCw2LDB6Ii8+Cjwvc3ZnPgo=);
  }

  .inner-message-container:hover .message-bubble .html-message-action > button {
    display: block;
}

  /* Вариант 1: Точный селектор для вашей структуры */
  .inner-message-container:hover .message-bubble > .html .html:has([data-status="error"]) .html-message-action > button[data-action="action:retry"] {
    display: block  !important;
}

  .message-bubble:has(.deep-chat-temporary-message .action-button-error) {
    background-color: var(--gray_white) !important;
    padding: 0px 0px 12px 0px;
}

.message-bubble .deep-chat-temporary-message .action-button-error {
    background-color: var(--gray_white) !important;
    border: 1px solid rgb(194, 194, 194);
    border-radius: 14px !important;
}
`
</script>

<template>
  <div class="Chat__wrapper">
    <deep-chat
      class="Chat"
      ref="chatElementRef"
      :connect="{
        stream: true,
        handler: connectHandler,
      }"
      :focusMode="false"
      :demo="{ displayLoading: { history: { small: false } } }"
      :remarkable="{ html: true, typographer: true }"
      :history="cacheMessages"
      :introPanelStyle="introPanelStyle"
      :textInput="{
        styles: {
          container: textInputContainerStyles,
          text: textInputStyles,
        },
        placeholder: {
          text: chatPlaceholder,
          style: inputPlaceholderStyle,
        },
      }"
      :errorMessages="errorMessages"
      :submitButtonStyles="{
        submit: {
          container: {
            default: {
              backgroundColor: 'initial',
              display: 'block',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              padding: '4px 8px',
            },
            hover: { backgroundColor: 'initial' },
            click: { backgroundColor: 'initial' },
          },
          svg: {
            styles: {
              default: {
                filter: 'brightness(100%) contrast(100%)',
                 position: 'absolute',
                 top: 'calc(50% - 6px)',
                 left: 'calc(50% - 5px)',
              },
            },
            content:
              '<svg width=&quot;10&quot; height=&quot;12&quot; viewBox=&quot;0 0 10 12&quot; fill=&quot;none&quot; xmlns=&quot;http://www.w3.org/2000/svg&quot;>\n' +
              '    <path d=&quot;M5 1.5L5 10.5M5 1.5L1 5.35714M5 1.5L9 5.35714&quot; stroke=&quot;#495B69&quot; stroke-width=&quot;1.4&quot; stroke-linecap=&quot;round&quot; stroke-linejoin=&quot;round&quot;/>\n' +
              '</svg>\n',
          },
        },
        stop: {
          container: { default: { display: 'none' } },
        },
      }"
      :htmlWrappers="{
        default:
          '<div class=\'html\'><div class=\'html-message-user html-wrapper\'></div><div class=\'html-message-action\'><button data-action=\'action:copy\' onclick=\'window.action(this)\'><i class=\'copy\'></i></button><button data-action=\'action:retry\' onclick=\'window.action(this)\'><i class=\'retry\' /></button></div></div>',
        ai: `<div class=\'html\'><div class=\'html-message-ai html-wrapper\'></div><div class=\'html-message-action\'><button data-action=\'action:copy\' onclick=\'window.action(this)\'><i class=\'copy\'></i></button></div></div>`,
        aiActions: `<div></div>`,
      }"
      :mixedFiles="fileConnect"
      :messageStyles="{
        default: {
          ai: {
            bubble: aiBubbleStyles,
          },
          user: {
            bubble: userBubbleStyles,
          },
        },
      }"
      :auxiliaryStyle="auxiliaryStyles"
      :htmlClassUtilities="{
        ['action-button']: {
          styles: {
            default: { padding: '3px 8px', cursor: 'pointer' },
          },
        },
      }"
      :attachmentContainerStyle="attachmentContainerStyles"
    >
      <div v-if="props.isShowGreeting && !isLoading">
        <div class="emptyContent">
          <slot />
        </div>
      </div>
    </deep-chat>

    <a-spin size="large" class="Chat__spinner" v-if="isLoading" />
  </div>
</template>

<style lang="scss" scoped>
.Chat__wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.Chat__spinner {
  position: absolute;
  top: calc(50% - 30px);
  left: calc(50% - 15px);
}

.Chat {
  width: 100% !important;
  height: calc(100vh - 70px) !important;
  border: none !important;
  display: block;

  .Chat__spinner {
    min-height: 200px;
    background: rgba(255, 255, 255, 0.9);
    z-index: 100;
  }

  #shadow-root {
    .command__button {
      border: none;
      box-shadow: initial;
      background-color: var(--gray_white);
    }
  }
}
</style>